import * as React from "react";
import { useCashOutPoints } from "@njoy/controller";
import { pointsValue } from "@njoy/common";

import { NewModal } from "../../../components/uiMerge/newModal";
import NewButton from "../../../components/uiMerge/newButton";
import ToastMessage from "../../../hooks/useToast";
import { useTenantConfig } from "../../../hooks/useTenantConfig";

const naira = (amount: number) => `₦${amount.toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

/** "Cash out" for sites with points-to-cash: points into the member's wallet. */
export const CashOutPoints: React.FC<{ balance: number }> = ({ balance }) => {
  const { config } = useTenantConfig();
  const { cashOut, loading } = useCashOutPoints();
  const [open, setOpen] = React.useState(false);
  const [points, setPoints] = React.useState("");

  const terms = config?.pointsToCash;
  const available = Math.floor(balance);
  if (!terms || !config?.features.wallet || available < Math.max(1, terms.minCashout)) {
    return null;
  }

  const amount = Number(points);
  const valid = Number.isInteger(amount) && amount >= Math.max(1, terms.minCashout) && amount <= available;

  const submit = async () => {
    const { result, error } = await cashOut(amount);
    if (error || !result) {
      ToastMessage("error", error || "Unable to cash out points right now.");
      return;
    }
    ToastMessage("success", result.message);
    setOpen(false);
    setPoints("");
  };

  return (
    <>
      <button
        type="button"
        className="mt-1 self-start text-xs font-semibold text-[color:var(--brand-primary)]"
        onClick={() => setOpen(true)}
      >
        Cash out points
      </button>
      <NewModal isOpen={open} onClose={() => setOpen(false)} roundedClass="rounded-[10px]" colorClass="bg-black" widthClass="w-[335px]" position="center">
        <div className="flex flex-col gap-3 p-6 text-white">
          <p className="text-[18px] font-extrabold">Cash out points</p>
          <p className="text-[13px] text-[#CECECF]">
            Each point is worth {naira(terms.pointValue)}. You have {available.toLocaleString("en-NG")} points
            {terms.minCashout > 0 ? `; the minimum is ${terms.minCashout.toLocaleString("en-NG")}` : ""}.
          </p>
          <input
            type="number"
            inputMode="numeric"
            min={Math.max(1, terms.minCashout)}
            max={available}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="Points to cash out"
            aria-label="Points to cash out"
            className="h-12 rounded-lg bg-[#1E1E1E] px-3 text-white outline-none"
          />
          <p className="text-[13px]">{valid ? `${naira(pointsValue(amount, terms.pointValue))} will go to your wallet.` : " "}</p>
          <div className="flex gap-4">
            <NewButton fullWidth variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </NewButton>
            <NewButton fullWidth onClick={submit} disabled={!valid || loading}>
              {loading ? "Cashing out..." : "Cash out"}
            </NewButton>
          </div>
        </div>
      </NewModal>
    </>
  );
};
